// Settings → Subscription — the organisation's own plan from Mitra One Care
// (owner, 1 Oct 2026 — doc/5.10.2026.md). Tenant admin only; not shown to a
// No fee organisation (Settings hides the tab).
//
//   - current plan: features, monthly or yearly, amount, start and end date;
//   - "Your plan ends on <date>. Contact Mitra One Care to renew.";
//   - every invoice with paid / unpaid, to view or download (a PDF drawn by
//     the server from the stored invoice).
//
// Read-only: plans are made and invoices marked paid by the Mitra One Care
// team. The per-feature price list is never sent here — only the features
// and the amounts on the invoice (GET /tenant-billing/mine).
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BadgeIndianRupee, CalendarClock, Download, Eye, Loader2, Receipt, AlertTriangle } from 'lucide-react';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const day = (d) => {
    if (!d) return '—';
    const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
    return y && m && dd ? `${dd} ${MONTHS[m - 1]} ${y}` : '—';
};
const inrFormatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
const inr = (n) => inrFormatter.format(Number(n) || 0);

const chip = {
    paid: 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/40',
    unpaid: 'bg-[#D7A05C]/15 text-[#8a5a1a] border-[#D7A05C]/40',
    void: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function TenantSubscriptionTab() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let live = true;
        createAuthedAxios().get(`${baseURL}/tenant-billing/mine`)
            .then((res) => { if (live) setData(res.data?.data || {}); })
            .catch(() => { if (live) setError(t('tenantSubscription.load_error', 'Could not load your subscription.')); });
        return () => { live = false; };
    }, [t]);

    const openPdf = async (invoiceNo, download) => {
        try {
            const res = await createAuthedAxios().get(`${baseURL}/tenant-billing/mine/invoices/${encodeURIComponent(invoiceNo)}/pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            if (download) {
                const link = document.createElement('a');
                link.href = url;
                link.download = `${invoiceNo}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            } else {
                window.open(url, '_blank', 'noopener');
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            }
        } catch {
            toast.error(t('tenantSubscription.pdf_error', 'Could not open the invoice.'));
        }
    };

    if (error) return <p className="text-sm text-red-600" data-testid="tenant-subscription-error">{error}</p>;
    if (!data) {
        return (
            <p className="flex items-center gap-2 text-sm text-gray-500" data-testid="tenant-subscription-loading">
                <Loader2 className="w-4 h-4 animate-spin" />{t('tenantSubscription.loading', 'Loading…')}
            </p>
        );
    }
    if (data.noFee) {
        return <p className="text-sm text-gray-600" data-testid="tenant-subscription-no-fee">{t('tenantSubscription.no_fee', 'Your organisation uses Mitra One Care free — there is no plan to pay for.')}</p>;
    }

    const state = data.state || {};
    const plan = data.currentPlan;
    const upcoming = data.upcomingPlan;
    const periodLabel = (p) => (p === 'yearly' ? t('tenantSubscription.yearly', 'Yearly') : t('tenantSubscription.monthly', 'Monthly'));

    return (
        <div className="space-y-4" data-testid="tenant-subscription-tab">
            {state.kind === 'none' && (
                <p className="text-sm text-gray-600" data-testid="tenant-subscription-none">{t('tenantSubscription.none', 'No plan has been set up for your organisation yet.')}</p>
            )}
            {state.kind === 'ended' && (
                <div className="rounded-2xl border border-[#D7A05C]/50 bg-[#D7A05C]/10 px-4 py-3 text-sm text-[#8a5a1a] flex items-center gap-2" data-testid="tenant-subscription-ended">
                    <AlertTriangle className="w-4 h-4 flex-none" />
                    {t('tenantSubscription.ended', 'Your plan ended on {{date}}. Contact Mitra One Care to renew.', { date: day(state.endedOn) })}
                </div>
            )}

            {plan && (
                <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5" data-testid="tenant-subscription-current">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3">
                        <BadgeIndianRupee className="w-5 h-5 text-[#2D8A8A]" />
                        <h3 className="text-[15px] font-bold text-gray-800">{t('tenantSubscription.current', 'Current plan')}</h3>
                        <span className="text-[12px] text-gray-500">{periodLabel(plan.period)} · {day(plan.startDate)} – {day(plan.coveredUntil || plan.endDate)}</span>
                        <span className="ml-auto text-base font-extrabold text-[#2D8A8A]" data-testid="tenant-subscription-amount">{inr(plan.total)}</span>
                    </div>
                    {state.kind === 'active' && !state.renewed && (
                        <p className={`flex items-center gap-1.5 text-[13px] mb-3 ${state.endingSoon ? 'text-[#8a5a1a] font-semibold' : 'text-gray-600'}`} data-testid="tenant-subscription-ends">
                            <CalendarClock className="w-4 h-4" />
                            {t('tenantSubscription.ends_on', 'Your plan ends on {{date}}. Contact Mitra One Care to renew.', { date: day(state.endsOn) })}
                        </p>
                    )}
                    {upcoming && (
                        <p className="text-[13px] text-gray-600 mb-3" data-testid="tenant-subscription-renewed">
                            {t('tenantSubscription.renewed', 'Renewed — your next plan starts on {{date}}.', { date: day(upcoming.startDate) })}
                        </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                        {plan.features.map((f) => (
                            <span key={f.featureId} className="text-[12px] text-gray-700">• {f.label}</span>
                        ))}
                    </div>
                </section>
            )}

            <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5" data-testid="tenant-subscription-invoices">
                <h3 className="text-[15px] font-bold text-gray-800 mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-[#2D8A8A]" />{t('tenantSubscription.billing', 'Billing')}
                </h3>
                {(data.invoices || []).length === 0 ? (
                    <p className="text-[13px] text-gray-500">{t('tenantSubscription.no_invoices', 'No invoices yet.')}</p>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {data.invoices.map((inv) => (
                            <div key={inv.invoiceNo} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5 text-[13px] text-gray-700" data-testid={`tenant-invoice-${inv.invoiceNo}`}>
                                <span className="font-semibold">{inv.invoiceNo}</span>
                                <span className="text-gray-500">{day(inv.periodStart)} – {day(inv.periodEnd)}</span>
                                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-semibold ${chip[inv.status] || chip.void}`} data-testid={`tenant-invoice-status-${inv.invoiceNo}`}>
                                    {inv.status === 'paid' ? t('tenantSubscription.paid', 'Paid')
                                        : inv.status === 'unpaid' ? t('tenantSubscription.unpaid', 'Unpaid')
                                            : t('tenantSubscription.void', 'Cancelled')}
                                </span>
                                <span className="ml-auto font-semibold tabular-nums">{inr(inv.total)}</span>
                                <button type="button" onClick={() => openPdf(inv.invoiceNo, false)} data-testid={`tenant-invoice-view-${inv.invoiceNo}`}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-[12px] font-semibold text-gray-600 hover:border-[#4ABDBD] hover:text-[#2D8A8A] cursor-pointer">
                                    <Eye className="w-3.5 h-3.5" />{t('tenantSubscription.view', 'View')}
                                </button>
                                <button type="button" onClick={() => openPdf(inv.invoiceNo, true)} data-testid={`tenant-invoice-download-${inv.invoiceNo}`}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-[12px] font-semibold text-gray-600 hover:border-[#4ABDBD] hover:text-[#2D8A8A] cursor-pointer">
                                    <Download className="w-3.5 h-3.5" />{t('tenantSubscription.download', 'Download')}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
